import axios from 'axios';
import React, { useEffect, useState } from 'react';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import leiOrcamentariaService, { TIPO_LEI_LABEL, type LeiOrcamentaria } from '../services/leiOrcamentariaService';
import { FIELD_CLASS } from './ui/fieldStyles';
import Button from './ui/Button';

type Props = {
  emendaId: string;
  exercicio?: number;
  leiOrcamentariaId?: string;
  tipo?: string;
  numero?: string;
  ano?: number;
  url?: string;
  canEdit: boolean;
  /** Permite ir ao cadastro de leis quando o exercício ainda não tem lei. */
  canManageLeis?: boolean;
  onSaved: (updated: EmendaDTO) => void;
};

const labelClass = 'block text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400';
const inputClass = FIELD_CLASS;

/**
 * Lei orçamentária (LOA ou crédito adicional) que dá suporte à emenda (TAEP B.2). O
 * Orçamento escolhe uma lei do cadastro, filtrada pelo exercício da emenda; o portal
 * da transparência publica número, ano e link da lei escolhida.
 */
const EmendaVinculoOrcamentarioPanel: React.FC<Props> = ({
  emendaId, exercicio, leiOrcamentariaId, tipo, numero, ano, url, canEdit, canManageLeis, onSaved,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [leis, setLeis] = useState<LeiOrcamentaria[]>([]);
  const [carregandoLeis, setCarregandoLeis] = useState(false);
  const [escolhida, setEscolhida] = useState('');

  const completo = !!(tipo && numero && ano && url);
  // Emenda antiga com o ato digitado à mão e sem lei do cadastro vinculada.
  const legado = completo && !leiOrcamentariaId;

  useEffect(() => {
    if (!editing) return;
    setCarregandoLeis(true);
    leiOrcamentariaService.listar({ exercicio, somenteAtivas: true })
      .then(setLeis)
      .catch(() => setErro('Não foi possível carregar as leis do exercício.'))
      .finally(() => setCarregandoLeis(false));
  }, [editing, exercicio]);

  const abrirEdicao = () => {
    setEscolhida(leiOrcamentariaId || '');
    setErro(null);
    setEditing(true);
  };

  const salvar = async () => {
    setErro(null);
    setSaving(true);
    try {
      const updated = await emendaService.atualizarVinculoOrcamentario(emendaId, escolhida || null);
      onSaved(updated);
      setEditing(false);
    } catch (err: unknown) {
      setErro((axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) || 'Não foi possível salvar o vínculo orçamentário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vínculo orçamentário</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Lei que dá suporte à emenda, escolhida no cadastro de leis do exercício. Publicado no portal da transparência.</p>
        </div>
        {canEdit && !editing && (
          <Button variant="secondary" onClick={abrirEdicao}>{completo ? 'Trocar lei' : 'Escolher lei'}</Button>
        )}
      </div>
      <div className="my-5 h-px bg-slate-200 dark:bg-slate-700" />

      {!editing ? (
        completo ? (
          <>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
              <div><dt className={labelClass}>Tipo do ato</dt><dd className="mt-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">{TIPO_LEI_LABEL[tipo || ''] || tipo}</dd></div>
              <div><dt className={labelClass}>Número / ano</dt><dd className="mt-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">{numero} / {ano}</dd></div>
              <div><dt className={labelClass}>Documento</dt><dd className="mt-1.5 text-sm"><a href={url} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline dark:text-blue-400">Abrir lei →</a></dd></div>
            </dl>
            {legado && (
              <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                Dados digitados antes do cadastro de leis. Escolha a lei correspondente para vinculá-la.
              </p>
            )}
          </>
        ) : (
          <p className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            Nenhuma lei vinculada. Escolha a lei orçamentária (LOA ou crédito adicional) do exercício {exercicio ?? ''}.
          </p>
        )
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="vinculo-lei" className={labelClass}>Lei do exercício {exercicio ?? ''}</label>
            <select id="vinculo-lei" value={escolhida} onChange={(e) => setEscolhida(e.target.value)} disabled={carregandoLeis} className={inputClass}>
              <option value="">{carregandoLeis ? 'Carregando...' : 'Sem lei vinculada'}</option>
              {leis.map((lei) => (
                <option key={lei.id} value={lei.id}>{`${lei.numero} — ${TIPO_LEI_LABEL[lei.tipo] ?? lei.tipo}`}</option>
              ))}
            </select>
            {!carregandoLeis && leis.length === 0 && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Nenhuma lei ativa cadastrada para {exercicio ?? 'este exercício'}.
                {canManageLeis && <> <a href="/dashboard/leis-orcamentarias" className="font-semibold text-blue-700 underline dark:text-blue-400">Cadastrar lei</a>.</>}
              </p>
            )}
          </div>
          {erro && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{erro}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => { setErro(null); setEditing(false); }} disabled={saving}>Cancelar</Button>
            <Button variant="primary" onClick={salvar} disabled={saving || carregandoLeis}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default EmendaVinculoOrcamentarioPanel;
